import { getLocalToday } from "utils/Common/Date";
import { createElement, useState } from "react";
import { type ReactElement } from "react";
import { pdf, type DocumentProps } from "@react-pdf/renderer";
import {
  getDrawTimeline,
  getDrawInspection,
  getDrawFullDetail,
} from "services/drawService";
import DrawHistoryPdf, {
  DrawHistoryEntry,
  DrawHistoryPdfProps,
} from "Components/Common/DrawHistoryPdf/DrawHistoryPdf";
import {
  DrawDetail,
  DrawListItem,
  DrawTimelineEvent,
  DrawInspectionData,
  GcProgressDisplayItem,
  buildGcProgressDisplayItems,
} from "Pages/Admin/ProjectDetail/sections/DrawHistory/DrawHistory.types";

// react-pdf's <Image> fetches the src itself, and browsers block that read
// when the photo host (e.g. an S3 bucket) doesn't send CORS headers for the
// app's origin — even though a plain <img> tag displays the same URL fine.
// Pre-fetching as a blob and inlining it as a data URI sidesteps react-pdf's
// loader; if the underlying fetch is itself CORS-blocked this still fails,
// which is the caller's cue to surface a "couldn't embed" warning.
const fetchAsDataUri = async (url?: string): Promise<string | null> => {
  if (!url) return null;
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
};

const buildTimelineAndInspection = async (
  projectId: string,
  drawId: string,
) => {
  const [timelineRes, inspectionRes] = await Promise.all([
    getDrawTimeline(projectId, drawId) as Promise<{
      data?: any;
      error?: any;
    }>,
    getDrawInspection(projectId, drawId) as Promise<{
      data?: any;
      error?: any;
    }>,
  ]);

  const timelinePayload = timelineRes.data?.data;
  const timelineEvents: DrawTimelineEvent[] = Array.isArray(
    timelinePayload?.events,
  )
    ? timelinePayload.events
    : Array.isArray(timelinePayload)
      ? timelinePayload
      : [];

  const inspectionData: DrawInspectionData | null = inspectionRes.error
    ? null
    : (inspectionRes.data?.data ?? null);

  const sourcePhotos = inspectionData?.photos ?? [];
  const resolvedPhotos = await Promise.all(
    sourcePhotos.map(async (photo) => ({
      ...photo,
      url: (await fetchAsDataUri(photo.url)) ?? undefined,
    })),
  );

  return {
    timelineEvents: timelineRes.error ? [] : timelineEvents,
    inspectionData: inspectionData
      ? { ...inspectionData, photos: resolvedPhotos }
      : null,
  };
};

const downloadPdf = async (props: DrawHistoryPdfProps, fileName: string) => {
  const el = createElement(
    DrawHistoryPdf,
    props,
  ) as ReactElement<DocumentProps>;
  const blob = await pdf(el).toBlob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

export const useDrawHistoryPdf = () => {
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const [isExportingAll, setIsExportingAll] = useState(false);

  const exportDrawHistoryPdf = async (
    projectId: string,
    drawDetail: DrawDetail | null,
    projectName?: string,
    progressLineItems?: GcProgressDisplayItem[],
    notify?: (message: string) => void,
  ) => {
    if (!projectId || !drawDetail?.draw?.id) return;
    setIsExportingPdf(true);
    try {
      const { timelineEvents, inspectionData } =
        await buildTimelineAndInspection(projectId, drawDetail.draw.id);

      const props: DrawHistoryPdfProps = {
        entries: [
          {
            drawDetail,
            timelineEvents,
            inspectionData,
            progressLineItems: progressLineItems ?? [],
          },
        ],
        projectName,
      };

      const drawLabel = String(drawDetail.draw.drawNumber).padStart(2, "0");
      await downloadPdf(
        props,
        `Draw-${drawLabel}-Full-Detail-${projectName ?? "export"}-${getLocalToday()}.pdf`,
      );
    } finally {
      setIsExportingPdf(false);
    }
  };

  const exportAllDrawsPdf = async (
    projectId: string,
    draws: DrawListItem[],
    projectName?: string,
    notify?: (message: string) => void,
  ) => {
    if (!projectId || draws.length === 0) return;
    setIsExportingAll(true);
    try {
      notify?.(
        "Preparing full draw history PDF for all draws — this may take a moment…",
      );

      const sortedDraws = [...draws].sort(
        (a, b) => a.drawNumber - b.drawNumber,
      );

      const entries: DrawHistoryEntry[] = [];
      for (const draw of sortedDraws) {
        const detailRes = (await getDrawFullDetail(projectId, draw.id)) as {
          data?: any;
          error?: any;
        };
        const drawDetail: DrawDetail | null = detailRes.error
          ? null
          : (detailRes.data?.data ?? null);
        if (!drawDetail) continue;

        const { timelineEvents, inspectionData } =
          await buildTimelineAndInspection(projectId, draw.id);

        entries.push({
          drawDetail,
          timelineEvents,
          inspectionData,
          progressLineItems: buildGcProgressDisplayItems(drawDetail),
        });
      }

      if (entries.length === 0) {
        notify?.("No draw data available to export.");
        return;
      }

      await downloadPdf(
        { entries, projectName },
        `Draw-History-Full-Detail-All-Draws-${projectName ?? "export"}-${getLocalToday()}.pdf`,
      );
    } finally {
      setIsExportingAll(false);
    }
  };

  return {
    exportDrawHistoryPdf,
    isExportingPdf,
    exportAllDrawsPdf,
    isExportingAll,
  };
};
